import clsx from 'clsx';
import PropTypes from 'prop-types';
import React from 'react';

import radialIconMapConfiguration from './images/radial-icon-map-configuration.svg';
import radialIconMapGlobal from './images/radial-icon-map-global.svg';
import radialIconMapIntegration from './images/radial-icon-map-integration.svg';
import radialIconMapSecurity from './images/radial-icon-map-security.svg';
import subscriptionJourneyCollaboration from './images/subscription-journey-collaboration.svg';
import subscriptionJourneyIntegration from './images/subscription-journey-integration.svg';
import subscriptionJourneyLifecycle from './images/subscription-journey-lifecycle.svg';
import subscriptionJourneyRenewal from './images/subscription-journey-renewal.svg';

const illustrations = {
  'radial-icon-map-configuration': radialIconMapConfiguration,
  'radial-icon-map-global': radialIconMapGlobal,
  'radial-icon-map-integration': radialIconMapIntegration,
  'radial-icon-map-security': radialIconMapSecurity,
  'subscription-journey-collaboration': subscriptionJourneyCollaboration,
  'subscription-journey-integration': subscriptionJourneyIntegration,
  'subscription-journey-lifecycle': subscriptionJourneyLifecycle,
  'subscription-journey-renewal': subscriptionJourneyRenewal,
};

const SectionWithSteps = ({
  isLightBackground,
  isCountVisible,
  isRightToLeft,
  title,
  description,
  items,
}) => (
  <section
    className={clsx(
      'section-with-steps py-40 xl:py-30 lg:py-28 md:py-20',
      !isLightBackground && 'bg-new-gray-2'
    )}
  >
    <div className="container">
      <h2
        className={clsx(
          'max-w-3xl mx-auto px-5 text-center text-new-black lg:max-w-xl md:px-0',
          isCountVisible ? 'fs-44 md:max-w-md' : 'fs-56 xl:max-w-[640px] md:text-[28px]'
        )}
      >
        {title}
      </h2>
      {description && (
        <p
          className={clsx(
            'fs-20 mt-5 mx-auto text-center text-pretty text-new-gray-1 xl:mt-4 lg:mt-3.5 md:mt-3',
            isCountVisible ? 'max-w-sm lg:max-w-xs' : 'max-w-xl px-3 lg:max-w-md'
          )}
        >
          {description}
        </p>
      )}
      <ol
        className={clsx(
          'relative flex flex-col pt-[72px] xl:gap-14 xl:pt-16 lg:pt-14',
          isCountVisible &&
            'before:absolute before:inset-y-20 before:left-1/2 before:h-auto before:w-0.5 before:-translate-x-px before:bg-new-divider before:bg-no-repeat before:bg-[length:2px_104%] xl:before:inset-y-0 xl:before:bg-[length:2px_101%] lg:before:inset-y-8 md:before:content-none',
          isCountVisible ? 'gap-5 md:gap-10 md:-mb-2' : 'gap-16 lg:gap-12 md:pt-12'
        )}
      >
        {items.map(({ illustrationName, title, description }, index) => {
          const isOddLTR =
            (index % 2 !== 0 && !isRightToLeft) || (index % 2 === 0 && isRightToLeft);

          return (
            <li
              className={clsx(
                'relative flex min-h-[500px] xl:min-h-[394px] lg:min-h-[300px] md:flex-col md:min-h-auto',
                'md:before:absolute md:before:top-0 md:before:left-1/2 md:before:h-12 md:before:w-0.5 md:before:-translate-x-px md:before:bg-line-gradient',
                isOddLTR && 'flex-row-reverse',
                isCountVisible && 'md:pt-13'
              )}
              key={index}
            >
              <div
                className={clsx(
                  'w-1/2 flex flex-col justify-center md:w-full md:mb-6 md:pl-0',
                  isCountVisible && isOddLTR && 'pl-28 xl:pl-20 lg:pl-[42px]',
                  !isCountVisible && isOddLTR && 'pl-24 xl:pl-11 lg:pl-8'
                )}
              >
                <div
                  className={clsx(
                    'max-w-lg md:max-w-96 md:mx-auto',
                    isCountVisible ? 'xl:max-w-sm lg:max-w-xs' : 'xl:max-w-md lg:max-w-96'
                  )}
                >
                  <h3 className="fs-36 text-balance text-new-black xl:text-[32px] lg:text-[24px] md:px-px md:text-[20px] md:text-center">
                    {title}
                  </h3>
                  {description && (
                    <p className="fs-20 mt-3 text-pretty text-new-gray-1 xl:text-[18px] lg:text-[16px] md:mt-2 md:text-center">
                      {description}
                    </p>
                  )}
                </div>
              </div>
              {isCountVisible > 0 && (
                <span className="relative self-center flex justify-center items-center size-8 -mt-2.5 pt-[3px] font-medium text-center text-[#26405A] bg-white/90 rounded-full shadow-[0_2px_6px_rgba(15,27,87,0.1)] lg:size-6 lg:-mt-0.5 lg:text-[14px] md:-order-1 md:-mt-2 md:mb-4">
                  {index + 1}
                </span>
              )}
              <div className="relative w-1/2 flex justify-center items-center md:w-full">
                <img
                  className={clsx(
                    'absolute top-1/2 -translate-y-1/2 md:static md:w-auto md:mt-0 md:translate-y-0 md:max-h-80',
                    isOddLTR && 'left-0 xl:-left-2',
                    !isOddLTR && 'right-0 xl:-right-2',
                    isCountVisible ? 'w-[81%] mt-2 xl:w-[88%]' : 'w-[90%] xl:w-full'
                  )}
                  src={illustrations[illustrationName]}
                  alt=""
                  loading="lazy"
                />
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  </section>
);

SectionWithSteps.propTypes = {
  isLightBackground: PropTypes.number,
  isCountVisible: PropTypes.number,
  isRightToLeft: PropTypes.number,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      illustrationName: PropTypes.oneOf(Object.keys(illustrations)).isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
    })
  ).isRequired,
};

SectionWithSteps.defaultProps = {
  isLightBackground: 0,
  isCountVisible: 0,
  isRightToLeft: 0,
  description: null,
};

export default SectionWithSteps;
